import { useState, useRef, useCallback, useEffect } from 'react'
import { ShoppingCart, GripVertical, UserPlus, Lock } from 'lucide-react'
import { CustomerHeader, type OpenCustomerDetailsOptions } from './CustomerHeader'
import { CartItemRow } from './CartItemRow'
import { CartSummary } from './CartSummary'
import { useIsMobile } from '@/hooks'
import { useProductBatches } from '@/api/products'
import { usePOSConfig } from '@/api/config'
import { computeSaleCap, isAtSaleCap } from '@/lib/cartCap'
import { usePOSStore, isReceiptPartiallyPaid } from '@/stores/posStore'
import { formatCurrency } from '@/lib/utils'
import type { Customer, CartItem, Product } from '@/types'

const MIN_WIDTH = 320
const MAX_WIDTH = 600
const DEFAULT_WIDTH = 384 // w-96

// Delivery-fee sentinel (DeliverySpecialProduct, id -123 on the BE). Its
// cart row is read-only; the keyboard +/- and remove handlers gate on it so
// the disabled-button bypass can't drop the fee. Mirrors the constant in
// CartItemRow / posStore.
const DELIVERY_PRODUCT_ID = -123

export interface RightSidebarProps {
  customer: Customer | null
  cart: CartItem[]
  onChangeCustomer: () => void
  onOpenCustomerDetails?: (customer: Customer, options?: OpenCustomerDetailsOptions) => void
  onQuantityChange: (itemId: string, delta: number) => void
  onRemoveFromCart: (itemId: string) => void
  onCheckout: () => void
  onProductClick?: (product: Product, cartItemId?: string) => void
  focusedCartIndex?: number
  onCartFocusChange?: (index: number) => void
  isMobileFullScreen?: boolean
}

export function RightSidebar({
  customer,
  cart,
  onChangeCustomer,
  onOpenCustomerDetails,
  onQuantityChange,
  onRemoveFromCart,
  onCheckout,
  onProductClick,
  focusedCartIndex = -1,
  onCartFocusChange,
  isMobileFullScreen = false,
}: RightSidebarProps) {
  const [width, setWidth] = useState(DEFAULT_WIDTH)
  const [isDragging, setIsDragging] = useState(false)
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set())
  const isResizing = useRef(false)
  const sidebarRef = useRef<HTMLElement>(null)
  const cartListRef = useRef<HTMLDivElement>(null)
  const isMobile = useIsMobile()

  // Sale-cap data for the keyboard-focused row. The qty `+` button on the
  // row itself is already disabled at cap via CartItemRow, but Shift+`=`
  // calls handleQuantityChange directly and would otherwise bypass it.
  // We resolve batches + config here so the keyboard handler can apply
  // the same gate. `useProductBatches` is per-product and React-Query-
  // deduped with the row's own call, so no extra network traffic.
  const focusedItem =
    focusedCartIndex >= 0 && focusedCartIndex < cart.length ? cart[focusedCartIndex] : null
  const focusedIsCannabis = focusedItem?.product?.is_cannabis === true
  const focusedHasLot = !!focusedItem?.lot_number
  const { data: focusedBatches } = useProductBatches(
    focusedItem?.product_id ?? 0,
    focusedIsCannabis && focusedHasLot,
  )
  const { data: posConfig } = usePOSConfig()
  const verifyOverOnHand = posConfig?.posVerifyIfIsOverOnHand !== false
  const focusedSaleCap = computeSaleCap(focusedItem, focusedBatches, verifyOverOnHand)

  // Partial-payment lock. CartItemRow disables its +/-/X buttons via the
  // same predicate, but the keyboard Shift+= / Shift+- shortcuts and the
  // handleRemove wrapper bypass the row-level disabled prop -- both
  // funnel through the handlers below, so we gate here too. Refund mode
  // is excluded; its mutations have their own per-line guards. posStore
  // mutators also gate on this -- single source of truth in posStore.
  const serverTotals = usePOSStore((s) => s.serverTotals)
  const isRefundMode = usePOSStore((s) => s.isRefundMode)
  const isPartiallyPaid = isReceiptPartiallyPaid(serverTotals, isRefundMode)
  const paidSoFar = isPartiallyPaid && serverTotals
    ? serverTotals.total - serverTotals.amountToPay
    : 0
  const amountRemaining = isPartiallyPaid && serverTotals ? serverTotals.amountToPay : 0

  const handleRemove = (itemId: string) => {
    // The delivery fee is part of the order — never removable. CartItemRow's
    // X is disabled, but the keyboard Shift+- path and a disabled-button
    // click reaching a parent both bypass that, so gate here too (before the
    // removal animation, so the row doesn't flash out then reappear).
    if (cart.find((i) => i.id === itemId)?.product_id === DELIVERY_PRODUCT_ID) return

    // Block edits on partially-paid receipts. CartItemRow's X is
    // already disabled here; this catches the keyboard Shift+- path
    // that decrements qty to 0 and calls handleRemove directly.
    if (isPartiallyPaid) return

    // Refund-mutation gate: two-stage check around the 250ms removal
    // animation. The deferred onRemoveFromCart would otherwise queue a
    // DELETE that runs *concurrently* with any PUT started during the
    // animation window (e.g. via keyboard Shift+= / Shift+-). The BE
    // serializes via with_for_update, but FE reseeds can land in
    // arbitrary order and leave serverTotals reflecting the wrong
    // post-mutation state -- the cashier could then commit Process
    // Refund against a stale figure.
    //
    // Entry stage: if a mutation is already in flight at click time,
    // refuse without starting the animation. Re-check stage: inside
    // the setTimeout, the in-flight state may have changed (a keyboard
    // qty change fired between click and timeout firing), so re-read
    // and skip the DELETE if so. In both refusal branches we clear
    // removingIds (no-op on entry; restores the row on re-check) so
    // the UI matches state: row stays in the cart, cashier sees it
    // didn't vanish, can click X again once the spinner clears.
    //
    // Sale-item path is unaffected: isRefundMutating is false in sale
    // mode, so both gates fall through.
    const item = cart.find((i) => i.id === itemId)
    const isRefundItem = !!item && item.quantity < 0
    if (isRefundItem && usePOSStore.getState().isRefundMutating) return

    setRemovingIds((prev) => new Set(prev).add(itemId))
    setTimeout(() => {
      const stillMutating =
        isRefundItem && usePOSStore.getState().isRefundMutating
      if (!stillMutating) {
        onRemoveFromCart(itemId)
      }
      setRemovingIds((prev) => {
        const next = new Set(prev)
        next.delete(itemId)
        return next
      })
    }, 250)
  }

  const handleQuantityChange = (itemId: string, delta: number) => {
    // Block edits on partially-paid receipts. The +/- buttons in
    // CartItemRow are already disabled here, but the keyboard Shift+=
    // / Shift+- shortcuts (effect below) call this directly.
    if (isPartiallyPaid) return

    const item = cart.find((i) => i.id === itemId)
    if (!item) return
    // Delivery fee qty is fixed at 1 (a -1 here would route to handleRemove).
    if (item.product_id === DELIVERY_PRODUCT_ID) return

    const isRefundItem = item.quantity < 0

    if (isRefundItem) {
      // Block refund-item qty changes while a prior PUT/DELETE is still
      // round-tripping to the BE. CartItemRow already greys out the
      // mouse +/- buttons via the same flag, but `handleQuantityChange`
      // is also invoked by the keyboard `+/=` and `-/_` shortcuts in
      // the effect below, which don't see the row's disabled state. A
      // held key or rapid press would otherwise queue parallel PUTs,
      // and the BE serializes via with_for_update but the network can
      // deliver responses out of order -- the older reseed then wins,
      // leaving cart-qty and serverTotals desynced.
      if (usePOSStore.getState().isRefundMutating) return

      // For refund items: plus (+) means refund more, minus (-) means refund less
      // Internally, quantity is negative (e.g., -1), so invert the delta:
      //   plus button (delta +1) → quantity goes from -1 to -2 (refund more)
      //   minus button (delta -1) → quantity goes from -1 to 0 (refund less → remove)
      const invertedDelta = -delta
      const newQuantity = item.quantity + invertedDelta

      // Keep at least 1 refund quantity in cart.
      // Use explicit remove action (X button) to remove refund lines.
      if (newQuantity >= 0) {
        return
      }

      // Cap: cannot refund more than originally purchased
      const maxRefund = item.max_refund_qty ?? Math.abs(item.quantity)
      if (Math.abs(newQuantity) > maxRefund) return

      onQuantityChange(itemId, invertedDelta)
    } else {
      // Normal sale items: original logic
      if (item.quantity + delta <= 0) {
        handleRemove(itemId)
      } else {
        onQuantityChange(itemId, delta)
      }
    }
  }

  const startResizing = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    isResizing.current = true
    setIsDragging(true)
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }, [])

  const startResizingTouch = useCallback((e: React.TouchEvent) => {
    e.preventDefault()
    isResizing.current = true
    setIsDragging(true)
    document.body.style.userSelect = 'none'
  }, [])

  const stopResizing = useCallback(() => {
    isResizing.current = false
    setIsDragging(false)
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
  }, [])

  const resize = useCallback((e: MouseEvent) => {
    if (!isResizing.current) return

    const newWidth = window.innerWidth - e.clientX
    if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) {
      setWidth(newWidth)
    }
  }, [])

  const resizeTouch = useCallback((e: TouchEvent) => {
    if (!isResizing.current) return

    const newWidth = window.innerWidth - e.touches[0].clientX
    if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) {
      setWidth(newWidth)
    }
  }, [])

  // Skip resize event listeners on mobile
  useEffect(() => {
    if (isMobile) return

    window.addEventListener('mousemove', resize)
    window.addEventListener('mouseup', stopResizing)
    window.addEventListener('touchmove', resizeTouch)
    window.addEventListener('touchend', stopResizing)
    return () => {
      window.removeEventListener('mousemove', resize)
      window.removeEventListener('mouseup', stopResizing)
      window.removeEventListener('touchmove', resizeTouch)
      window.removeEventListener('touchend', stopResizing)
    }
  }, [isMobile, resize, resizeTouch, stopResizing])

  // Scroll focused cart item into view
  useEffect(() => {
    if (focusedCartIndex >= 0 && cartListRef.current) {
      const child = cartListRef.current.children[focusedCartIndex] as HTMLElement
      child?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [focusedCartIndex])

  // Keyboard shortcuts for cart quantity (Shift+/-)
  useEffect(() => {
    if (focusedCartIndex < 0 || focusedCartIndex >= cart.length) return

    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      if (!e.shiftKey) return

      const item = cart[focusedCartIndex]
      if (!item) return

      // Partial-payment lock read at keystroke time (not via the
      // closure) to dodge a stale-closure bug: the effect doesn't
      // re-register on isPartiallyPaid changes (it'd otherwise need
      // handleQuantityChange in the deps and re-register on every
      // render). Reading the store directly here always sees the
      // latest payment state. posStore.updateQuantity / removeFromCart
      // also guard at the data layer, but checking here surfaces the
      // lock as a silent no-op consistent with the button path.
      const ps = usePOSStore.getState()
      if (isReceiptPartiallyPaid(ps.serverTotals, ps.isRefundMode)) return

      if (e.key === '+' || e.key === '=') {
        e.preventDefault()
        // Sale lines: gate against on-hand cap so Shift+= can't walk past
        // the same limit the row's `+` button enforces. Refund lines are
        // capped by `handleQuantityChange` itself (max_refund_qty).
        // Gift cards / baskets stay 1:1 — block any qty change here.
        const isSaleLine = item.quantity > 0
        const isGiftCard = !!item.gift_card_number
        const isBasket = (item.basket_id ?? null) !== null
        if (isSaleLine && (isGiftCard || isBasket)) return
        if (isSaleLine && isAtSaleCap(item.quantity, focusedSaleCap)) return
        handleQuantityChange(item.id, 1)
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault()
        // Gift cards / baskets are 1:1; block decrements (use X to remove).
        const isSaleLine = item.quantity > 0
        const isGiftCard = !!item.gift_card_number
        const isBasket = (item.basket_id ?? null) !== null
        if (isSaleLine && (isGiftCard || isBasket)) return
        handleQuantityChange(item.id, -1)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleQuantityChange is intentionally read via closure; partial-payment lock is read fresh from the store inside the handler.
  }, [focusedCartIndex, cart, onQuantityChange, onRemoveFromCart, onCartFocusChange, focusedSaleCap])

  return (
    <aside
      ref={sidebarRef}
      style={isMobileFullScreen ? undefined : { width }}
      className={
        isMobileFullScreen
          ? 'flex-1 flex flex-col bg-white min-h-0'
          : 'bg-white border-l border-gray-200 flex flex-col relative'
      }
    >
      {/* Resize handle — desktop only */}
      {!isMobile && (
        <>
          <div
            onMouseDown={startResizing}
            onTouchStart={startResizingTouch}
            className={`absolute left-0 top-0 bottom-0 w-0.5 -translate-x-1/2 cursor-col-resize transition-colors z-10 ${isDragging ? 'bg-green-500' : ''}`}
          />
          <div
            onMouseDown={startResizing}
            onTouchStart={startResizingTouch}
            className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full p-1.5 transition-colors cursor-col-resize z-20 ${isDragging
              ? 'bg-green-50 border-2 border-green-500'
              : 'bg-white border border-gray-200 hover:bg-gray-50'
              }`}
          >
            <GripVertical size={12} className={isDragging ? 'text-green-600' : 'text-gray-600'} />
          </div>
        </>
      )}

      {customer ? (
        <CustomerHeader
          customer={customer}
          onChangeCustomer={onChangeCustomer}
          onOpenDetails={onOpenCustomerDetails}
        />
      ) : (
        <div className="p-4 border-b border-gray-200">
          <button
            onClick={onChangeCustomer}
            disabled={isPartiallyPaid}
            title={isPartiallyPaid
              ? 'Receipt has payment(s) applied — void the payment in Checkout to add a customer'
              : undefined}
            className="flex items-center gap-2 w-full justify-center py-3 bg-gray-50 rounded-xl text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer disabled:bg-gray-100 disabled:text-gray-300 disabled:cursor-not-allowed"
          >
            <UserPlus size={20} />
            <span className="font-medium">Add Customer</span>
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto bg-gray-50 p-4">
        {/* Partial-payment banner. Renders only when the receipt has at
            least one payment applied but isn't fully paid yet. Tells the
            cashier "money already sits on this ledger -- don't edit the
            items, just take the remaining tender". Pairs with the
            paymentLocked guards in CartItemRow + the handleRemove /
            handleQuantityChange guards above. Real production case that
            motivated this: Ingersoll 2026-05-26 receipt
            S099R296-260526-00017 -- $28.75 paid against a 2-item $54.16
            cart, cashier saw 1 item / "Checkout $54.16" after the cart
            desynced and resorted to a standalone Moneris transaction. */}
        {isPartiallyPaid && (
          <div className="mb-3 bg-amber-50 border border-amber-300 rounded-xl p-3">
            <div className="flex items-center gap-2 text-amber-900">
              <Lock size={16} className="flex-shrink-0" />
              <span className="font-semibold text-sm">
                {formatCurrency(paidSoFar)} paid · {formatCurrency(amountRemaining)} remaining
              </span>
            </div>
            <p className="text-xs text-amber-800 mt-1 ml-6">
              Cart locked. Checkout to continue, or void payment.
            </p>
          </div>
        )}
        {cart.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-300">
            <ShoppingCart size={32} className="mb-2" />
            <p className="font-medium">Cart is empty</p>
            <p className="text-sm">Add products to get started</p>
          </div>
        ) : (
          <div ref={cartListRef}>
            {cart.map((item, index) => {
              const isRefundItem = item.quantity < 0
              // Refund cap. The sale-side on-hand cap lives in CartItemRow
              // so it can read the locked lot's `batch.quantity` directly
              // — different lots on the same SKU need to split as separate
              // receipt lines, each capped to its own batch's stock.
              const canIncrease = isRefundItem
                ? Math.abs(item.quantity) < (item.max_refund_qty ?? Math.abs(item.quantity))
                : true
              const isRemoving = removingIds.has(item.id)

              return (
                <div
                  key={item.id}
                  className={`cart-item-wrapper mb-3 last:mb-0 p-[3px] ${isRemoving ? 'removing' : ''}`}
                >
                  <CartItemRow
                    item={item}
                    onQuantityChange={handleQuantityChange}
                    onRemove={handleRemove}
                    onProductClick={(product, cartItemId) =>
                      onProductClick?.({ ...product, lot_number: item.lot_number ?? undefined }, cartItemId)
                    }
                    canIncrease={canIncrease}
                    isRemoving={isRemoving}
                    isFocused={index === focusedCartIndex}
                  />
                </div>
              )
            })}
          </div>
        )}
      </div>

      <CartSummary cart={cart} customer={customer} onCheckout={onCheckout} />
    </aside>
  )
}
